const _ = require('underscore/underscore-min');
const Utils = require('core-helpers/utils.js');
const PurchaseService = require('purchase/purchase-service.js');
const SharedDataService = require('data/shared-data-service.js');

const PlansService = (function(root, doc, undefined) {

    'use strict';

    const filename = 'plans-service.js';

    const getTranslation = (key) => {
        return root.BUSUU_TRANSLATIONS[key] || 'TRS NOT FOUND';
    };

    const isDefined = (val) => {
        return Boolean((val !== undefined) && (val !== null));
    };

    /**
     * Return the saving amount in percent
     * regarding the most expensive plan based on total price.
     * @param  {Integer} savingPercent
     * @return {String || null}
     */
    const getSavingPercent = (savingPercent) => {
        if (!savingPercent || isNaN(savingPercent)) {
            return 0;
        }
        return savingPercent;
    };

    /**
     * Returns a current discount if available
     * @param  {String} discountPercent
     * @return {Number}
     */
    const getAppliedDiscount = (discountPercent) => {

        if ((!discountPercent) || (isNaN(discountPercent))) {
            return 0;
        }

        discountPercent = parseFloat(discountPercent);

        if (discountPercent >= 100) {
            discountPercent = 100;
        }
        if (discountPercent <= 0) {
            discountPercent = 0;
        }

        return discountPercent;

    };

    /**
     * Returns all discount data if a discount has been found
     * on a given plan.
     * @param  {Object} plan
     * @return {Object || null}
     */
    const getDiscountInfo = (originalPlan) => {

        const appliedDiscount = getAppliedDiscount(originalPlan.discountPercentage);
        if (appliedDiscount <= 0) {
            return null;
        }

        try {
            return {
                hasDiscount: true,
                discountPercent: appliedDiscount,
                totalDiscountPrice: originalPlan.discountAmount.humanFormats.symbol,
                monthlyPriceAfterDiscount: originalPlan.afterDiscountMonthlyPrice.humanFormats.symbol,
                totalPriceAfterDiscount: originalPlan.afterDiscountPrice.humanFormats.symbol
            };
        } catch (e) {
            throw new Error(`Discount data is not properly formatted: ${ e }`);
        }

    };

    /**
     * This function verifies if all the
     * properties values are truthy within a given object.
     * Returns the undefined properties if found.
     * @param {Object}
     * @return {Boolean || Array}
     */
    const getUndefinedProperties = (object) => {
        return _.reduce(object, (previous, value, prop) => {
            const pass = (object.hasOwnProperty(prop) && isDefined(value));
            if (!pass) {
                previous.push(prop);
            }
            return previous;
        }, []);
    };

    /**
     * Returns true if user is recognised as authenticated
     * within the app. It's based on the datas the backend
     * is returning when we call the available plans endpoint.
     * @param {Object}
     * @return {Boolean}
     */
    const isUserAuthenticated = (plansData) => {
        return Boolean(plansData.uid && plansData.busuuTransactionId && plansData.currency && plansData.country);
    };

    /**
     * Verify if the current session is still available
     * and stores the selected plan data within browser storage.
     * This endpoint also returns the zuora object needed for
     * the account subscription in the CC method.
     * @param {Object} plansData
     * @param {Element} selectedPlan
     * @return {Promise}
     */
    const verifySessionAndStorePlan = (plansData, selectedPlan) => {

        const data = Utils.deepClone(plansData);

        const onSuccess = (zuoraData) => {
            const planToStore = getPlanToStore(data, zuoraData, selectedPlan);
            SharedDataService.storeSelectedPlan(planToStore);
            return zuoraData;
        };

        data.planID = selectedPlan.getAttribute('data-plan-id');
        return PurchaseService.isSessionAvailable(data.busuuTransactionId, data.planID).then(onSuccess);

    };

    /**
     * Returns an Object that will be stored inside
     * the browser session. This object contains all the data
     * to create a payment on the /purchase page.
     * This Object is based on the selected (clicked) plan.
     * @param {Object} plansData
     * @param {Object} zuoraData
     * @param {Element} selectedPlan
     * @return {Object}
     */
    const getPlanToStore = (plansData, zuoraData, selectedPlan) => {

        const data = {
            uid: plansData.uid,
            country: plansData.country,
            currency: plansData.currency,
            email: plansData.email || null,
            phone: plansData.msisdn || null,
            busuuTransactionId: plansData.busuuTransactionId,
            zuora: Utils.deepClone(zuoraData),
            plan: {
                hasDiscount: false,
                planID: plansData.planID,
                totalPrice: selectedPlan.getAttribute('data-total-price'),
                months: selectedPlan.getAttribute('data-months')
            }
        };

        const discountPercent = selectedPlan.getAttribute('data-discount');
        const totalDiscountPrice = selectedPlan.getAttribute('data-total-discount-price');
        const totalPriceAfterDiscount = selectedPlan.getAttribute('data-total-price-after-discount');

        if (discountPercent && totalDiscountPrice && totalPriceAfterDiscount) {
            data.plan.hasDiscount = true;
            data.plan.discountPercent = discountPercent;
            data.plan.totalDiscountPrice = totalDiscountPrice;
            data.plan.totalPriceAfterDiscount = totalPriceAfterDiscount;
        }

        const undefinedProps = getUndefinedProperties(data.plan);
        if (undefinedProps.length) {
            throw new Error(`${ undefinedProps.join(', ') } prop(s) are missing.`);
        }

        return data;

    };

    /**
     * Returns a formatted plan Object from the Original Backend plan.
     * There is all the needed informations to display each price plan.
     * This is a middleware between the Backend API and the Front-end.
     * It also checks if all values are defined and truphy.
     * @param {Object} originalPlan
     * @return {Object}
     */
    const getFormattedPlan = (originalPlan) => {

        let obj = null;
        try {

            obj = {
                hasDiscount: false,
                planID: originalPlan.plan.id,
                months: originalPlan.plan.months,
                monthlyPrice: originalPlan.plan.monthlyPrice.humanFormats.symbol,
                totalPrice: originalPlan.plan.price.humanFormats.symbol
            };

            const savingPercent = getSavingPercent(originalPlan.plan.saving);
            if (savingPercent > 0) {
                obj.savingPercent = savingPercent;
            }

            const discounts = getDiscountInfo(originalPlan);
            if (discounts) {
                obj = _.extend(obj, discounts);
                obj = Utils.deepClone(obj);
            }

            const undefinedProps = getUndefinedProperties(obj);
            if (undefinedProps.length) {
                throw new Error(`${ undefinedProps.join(', ') } prop(s) are missing.`);
            }

        } catch (e) {
            throw new Error(`Plan data is incorrect: ${ e }`);
        }

        return obj;

    };

    return {
        getFormattedPlan: getFormattedPlan,
        isUserAuthenticated: isUserAuthenticated,
        verifySessionAndStorePlan: verifySessionAndStorePlan
    };

})(window, document);

module.exports = PlansService;
